import * as React from "react"
import { useEffect, useMemo, useState, useCallback } from "react"
import {
	Alert, Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog,
	DialogActions, DialogContent, DialogTitle, Divider, Drawer,
	FormControl, IconButton, InputAdornment, InputLabel, Link, ListSubheader,
	Menu, MenuItem, Paper, Select, Stack,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TextField, Tooltip, Typography,
} from "@mui/material"
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CloseIcon from '@mui/icons-material/Close'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import StorefrontIcon from '@mui/icons-material/Storefront'
import GridViewIcon from '@mui/icons-material/GridView'
import EmailIcon from '@mui/icons-material/Email'
import HistoryIcon from '@mui/icons-material/History'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { alpha } from '@mui/material/styles'

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"
import SectionHistoryDrawer from "../SectionHistoryDrawer"

import { vtApi as api } from "../api"
import {
	retailers, totals, sections, taggers,
	drawerRetailerId,
	retailerSearch, retailerCountry, retailerFilters,
	retailerAssignee, selectedRetailerIds,
	sectionSearch, sectionFilters,
	clearAdminSignals,
} from "./@signals"


// Distinct country codes present in the loaded retailers, sorted, for
// the country filter dropdown. Uppercased for display.
function _countryOptions(rows) {
	const set = new Set()
	for (const r of (rows || [])) if (r.country) set.add(String(r.country).toUpperCase())
	return Array.from(set).sort()
}


// Mapper options for the FILTER: the union of the assignable taggers and
// whoever currently owns a loaded retailer. Without the second half, sites
// owned by someone who is no longer a tagger (or got disabled) would be
// unfindable. `assignable` marks the ones the back would actually accept as
// a bulk target — the assign dropdown greys out the rest.
function _assigneeOptions(rows, taggerList) {
	const byId = new Map()
	for (const t of (taggerList || [])) {
		if (t?.id_user == null) continue
		byId.set(t.id_user, { id_user: t.id_user, email: t.email, assignable: true })
	}
	for (const r of (rows || [])) {
		const qa = r?.qa
		if (!qa || qa.id_user == null || byId.has(qa.id_user)) continue
		byId.set(qa.id_user, { id_user: qa.id_user, email: qa.email, assignable: false })
	}
	return Array.from(byId.values())
		.sort((a, b) => String(a.email || '').localeCompare(String(b.email || '')))
}


// Max ids the bulk endpoint accepts per request (`VT_BULK_ASSIGN_CAP` in
// api_admin.py). Bigger selections are sliced into sequential requests —
// each request is atomic, the slicing is not.
const BULK_ASSIGN_CHUNK = 50

const _chunk = (arr, size) => {
	const out = []
	for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
	return out
}


const SLUG_RE = /^(?:[a-z0-9]|[a-z0-9][a-z0-9_-]{0,62}[a-z0-9])$/
const SLUG_HELP = "lowercase letters, digits, _ or -"
const isValidSlug = (s) => SLUG_RE.test(s)


function _cascadeMsg(prefix, cascade) {
	if (!cascade) return prefix
	const parts = []
	if (cascade.sections) parts.push(`${cascade.sections} section${cascade.sections === 1 ? '' : 's'}`)
	if (cascade.tasks)    parts.push(`${cascade.tasks} task${cascade.tasks === 1 ? '' : 's'}`)
	if (cascade.runs)     parts.push(`${cascade.runs} run${cascade.runs === 1 ? '' : 's'}`)
	return parts.length ? `${prefix} (also: ${parts.join(', ')})` : prefix
}


// ────────────────────────────────────────────────────────────────────
// Page entry
// ────────────────────────────────────────────────────────────────────
export default function AdminPage() {
	const { showToast } = useAppToast()
	const [createOpen, setCreateOpen] = useState(false)
	const [bulkOpen, setBulkOpen] = useState(false)

	const loadRetailers = useCallback(async () => {
		const r = await api.get(`${BACKEND_APIS.VT}/admin/retailers`)
		if (!r.ok) return showToast.error('Failed to load retailers: ' + r.error)
		retailers.value = r.data?.items || []
		totals.value = r.data?.totals || { retailers: 0, sections: 0, newsletters: 0 }
	}, [])

	const loadTaggers = useCallback(async () => {
		const r = await api.get(`${BACKEND_APIS.VT}/admin/taggers`)
		if (!r.ok) return showToast.error('Failed to load taggers: ' + r.error)
		taggers.value = r.data?.items || []
	}, [])

	const loadSections = useCallback(async (id_retailer) => {
		if (!id_retailer) { sections.value = []; return }
		const r = await api.get(`${BACKEND_APIS.VT}/admin/retailers/${id_retailer}/sections`)
		if (!r.ok) return showToast.error('Failed to load sections: ' + r.error)
		sections.value = r.data?.items || []
	}, [])

	useEffect(() => {
		loadRetailers()
		loadTaggers()
		return clearAdminSignals
	}, [])

	useEffect(() => { loadSections(drawerRetailerId.value) }, [drawerRetailerId.value])

	const openRetailer = (r) => { drawerRetailerId.value = r.id_retailer }
	const closeDrawer  = ()  => { drawerRetailerId.value = null; sections.value = [] }

	return <PageWrapper
		{...vtPageProps('Admin', 'Retailers, sections and QA assignment')}
	>
		<StatStrip />
		<RetailersToolbar
			onAdd={() => setCreateOpen(true)}
			onBulk={() => setBulkOpen(true)}
		/>
		<BulkAssignBar
			showToast={showToast}
			onChanged={loadRetailers}
		/>
		<RetailersTable
			showToast={showToast}
			openRetailer={openRetailer}
			onChanged={loadRetailers}
		/>

		<SectionsDrawer
			open={Boolean(drawerRetailerId.value)}
			onClose={closeDrawer}
			showToast={showToast}
			onChanged={() => {
				// Refresh BOTH the drawer's section list AND the
				// retailers table — toggling a section's `active`
				// changes the "N/M sections" badge on the retailer
				// row, so it'd lie until the next full page load
				// without this second call.
				loadSections(drawerRetailerId.value)
				loadRetailers()
			}}
		/>

		<CreateRetailerDialog
			open={createOpen}
			onClose={() => setCreateOpen(false)}
			showToast={showToast}
			onChanged={loadRetailers}
		/>

		<BulkImportDialog
			open={bulkOpen}
			onClose={() => setBulkOpen(false)}
			showToast={showToast}
			onChanged={loadRetailers}
		/>
	</PageWrapper>
}


// ────────────────────────────────────────────────────────────────────
// Top stat strip — global totals from the back's `/admin/retailers`
// response. Keeps the operator oriented in volume terms before they
// dive into filters.
// ────────────────────────────────────────────────────────────────────
function StatStrip() {
	const t = totals.value
	const cards = [
		{ label: 'Retailers',   value: t.retailers,   icon: <StorefrontIcon />, tint: 'primary' },
		{ label: 'Sections',    value: t.sections,    icon: <GridViewIcon />,   tint: 'info' },
		{ label: 'Newsletters', value: t.newsletters, icon: <EmailIcon />,      tint: 'success' },
	]
	return <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
		{cards.map(c => (
			<Paper
				key={c.label}
				variant="outlined"
				sx={{
					px: 2.5, py: 1.5, minWidth: 200, flex: '1 1 200px',
					display: 'flex', alignItems: 'center', gap: 1.5,
					bgcolor: (t) => alpha(t.palette[c.tint].main, 0.06),
					borderColor: (t) => alpha(t.palette[c.tint].main, 0.25),
				}}
			>
				<Box sx={{
					width: 40, height: 40, borderRadius: 1.5,
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					bgcolor: (t) => alpha(t.palette[c.tint].main, 0.15),
					color: `${c.tint}.main`,
				}}>
					{c.icon}
				</Box>
				<Box>
					<Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}>
						{c.label}
					</Typography>
					<Typography variant="h5" sx={{ lineHeight: 1.1, fontWeight: 700 }}>
						{Number(c.value || 0).toLocaleString()}
					</Typography>
				</Box>
			</Paper>
		))}
	</Stack>
}


// ────────────────────────────────────────────────────────────────────
// Retailers — toolbar + table
// ────────────────────────────────────────────────────────────────────
function RetailersToolbar({ onAdd, onBulk }) {
	const f = retailerFilters.value
	const setFilter = (k) => () => { retailerFilters.value = { ...f, [k]: !f[k] } }
	const activeCount = Object.values(f).filter(Boolean).length
	const assigneeOptions = _assigneeOptions(retailers.value, taggers.value)

	// After reassigning everything away from mapper A, A disappears from the
	// options while the filter still holds their id — MUI would render an
	// empty select over an empty table with no explanation. Reset instead.
	useEffect(() => {
		const v = retailerAssignee.value
		if (!v || v === 'none') return
		if (!assigneeOptions.some(o => String(o.id_user) === v)) retailerAssignee.value = ''
	}, [retailers.value, taggers.value])

	return <Paper
		variant="outlined"
		sx={{
			p: 1.25, mb: 2,
			bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
			borderColor: (t) => alpha(t.palette.primary.main, 0.12),
		}}
	>
		<Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
			<TextField
				size="small"
				placeholder="Search slug, name, id…"
				value={retailerSearch.value}
				onChange={e => { retailerSearch.value = e.target.value }}
				sx={{ minWidth: 280, flex: '0 0 auto', bgcolor: 'background.paper' }}
				InputProps={{
					startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
					endAdornment: retailerSearch.value ? (
						<InputAdornment position="end">
							<IconButton size="small" onClick={() => { retailerSearch.value = '' }}>
								<ClearIcon fontSize="small" />
							</IconButton>
						</InputAdornment>
					) : null,
				}}
			/>
			<TextField
				size="small" select label="Country"
				value={retailerCountry.value}
				onChange={e => { retailerCountry.value = e.target.value }}
				sx={{ width: 140, bgcolor: 'background.paper' }}
			>
				<MenuItem value="">All</MenuItem>
				{_countryOptions(retailers.value).map(cc => (
					<MenuItem key={cc} value={cc}>{cc}</MenuItem>
				))}
			</TextField>
			<TextField
				size="small" select label="Mapper"
				value={retailerAssignee.value}
				onChange={e => { retailerAssignee.value = e.target.value }}
				sx={{ width: 200, bgcolor: 'background.paper' }}
			>
				<MenuItem value="">All mappers</MenuItem>
				<MenuItem value="none">Unassigned</MenuItem>
				{assigneeOptions.map(o => (
					<MenuItem key={o.id_user} value={String(o.id_user)}>{o.email}</MenuItem>
				))}
			</TextField>
			<Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
			<Typography variant="caption" color="text.secondary" sx={{ pr: 0.5 }}>
				Filter{activeCount > 0 ? ` · ${activeCount}` : ''}
			</Typography>
			<FilterChip label="Active"         active={f.activeOnly}    onClick={setFilter('activeOnly')} />
			<FilterChip label="Has Puig"       active={f.hasPuig}       onClick={setFilter('hasPuig')}       color="info" />
			<FilterChip label="Has Retail"    active={f.hasRetail}     onClick={setFilter('hasRetail')}    color="info" />
			<FilterChip label="Missing Puig"   active={f.missingPuig}   onClick={setFilter('missingPuig')}   color="warning" />
			<FilterChip label="Missing Retail" active={f.missingRetail} onClick={setFilter('missingRetail')} color="warning" />
			<FilterChip label="With QA"        active={f.withQa}        onClick={setFilter('withQa')} />
			<FilterChip label="Without QA"     active={f.withoutQa}     onClick={setFilter('withoutQa')} />
			<FilterChip label="Newsletter"     active={f.hasNewsletter} onClick={setFilter('hasNewsletter')} color="success" />
			<FilterChip label="No sections"    active={f.noSections}    onClick={setFilter('noSections')}    color="warning" />
			<Box sx={{ flex: 1 }} />
			<Button variant="outlined" onClick={onBulk} startIcon={<UploadFileIcon />} sx={{ bgcolor: 'background.paper' }}>
				Bulk import
			</Button>
			<Button variant="contained" onClick={onAdd} startIcon={<AddIcon />}>
				Add retailer
			</Button>
		</Stack>
	</Paper>
}


function FilterChip({ label, active, onClick, color = 'primary' }) {
	return <Chip
		size="small"
		label={label}
		color={active ? color : 'default'}
		variant={active ? 'filled' : 'outlined'}
		onClick={onClick}
		sx={{
			fontWeight: active ? 600 : 400,
			bgcolor: active ? undefined : 'background.paper',
		}}
	/>
}


function filterRetailer(r, search, f, country, assignee) {
	if (!r) return false
	if (country && String(r.country || '').toUpperCase() !== String(country).toUpperCase()) return false
	if (assignee === 'none' && r.id_user_assigned != null) return false
	if (assignee && assignee !== 'none'
		&& String(r.id_user_assigned ?? '') !== String(assignee)) return false
	if (f.activeOnly && !r.active) return false
	if (f.hasPuig       && r.puig_id_site   == null) return false
	if (f.hasRetail     && r.retail_id_site == null) return false
	if (f.missingPuig   && r.puig_id_site   != null) return false
	if (f.missingRetail && r.retail_id_site != null) return false
	if (f.withQa        && !r.qa)  return false
	if (f.withoutQa     &&  r.qa)  return false
	if (f.hasNewsletter && !r.has_newsletter) return false
	if (f.noSections    && (r.section_count || 0) > 0) return false
	if (!search) return true
	const q = String(search).toLowerCase()
	return (
		(r.slug || '').toLowerCase().includes(q)
		|| (r.name || '').toLowerCase().includes(q)
		|| String(r.puig_id_site || '').includes(q)
		|| String(r.retail_id_site || '').includes(q)
		|| String(r.id_retailer || '').includes(q)
	)
}


// ────────────────────────────────────────────────────────────────────
// Bulk assign bar — only mounted while something is selected
// ────────────────────────────────────────────────────────────────────
function BulkAssignBar({ showToast, onChanged }) {
	const [target, setTarget] = useState('')
	const [busy, setBusy] = useState(false)
	const selected = selectedRetailerIds.value
	if (selected.length === 0) return null

	const options = _assigneeOptions(retailers.value, taggers.value)
	const assignable = options.filter(o => o.assignable)
	const unassignable = options.filter(o => !o.assignable)

	// A selection survives filter changes on purpose (so it can be built up
	// across filters), but the header checkbox only ever ticks the filtered
	// rows — so Assign can act on sites the operator isn't looking at. Spell
	// the split out rather than hide it.
	const visibleIds = new Set(retailers.value
		.filter(r => filterRetailer(
			r, retailerSearch.value, retailerFilters.value, retailerCountry.value, retailerAssignee.value,
		))
		.map(r => r.id_retailer))
	const outside = selected.filter(id => !visibleIds.has(id)).length

	const run = async (id_user, label) => {
		setBusy(true)
		let updated = 0, rehomed = 0, missing = 0, failed = []
		// The back caps `ids` per request, so a bigger selection goes in
		// slices. Each request is atomic on its own; the slicing is not —
		// on a mid-way failure, report what already went through.
		for (const slice of _chunk(selected, BULK_ASSIGN_CHUNK)) {
			const res = await api.patch(
				`${BACKEND_APIS.VT}/admin/retailers/assign-user`,
				{ ids: slice, id_user },
			)
			if (!res.ok) {
				setBusy(false)
				// A 500 or a timeout carries no `error` body, and the request may
				// well have committed server-side before the connection broke —
				// that is exactly what happened on 2026-08-11. Never let the
				// operator read a failure as "nothing changed": the list is
				// reloaded right below, so point them at it instead of retrying
				// blind on top of a request that might still be running.
				showToast.error([
					res.error || 'The request failed without a reason',
					updated ? `${updated} site(s) had already been applied` : null,
					'the list has been refreshed — check it before retrying',
				].filter(Boolean).join(' — '))
				selectedRetailerIds.value = []
				return onChanged()
			}
			updated += res.data?.updated || 0
			rehomed  += res.data?.tasks_rehomed || 0
			missing  += (res.data?.missing || []).length
			failed = failed.concat(res.data?.failed || [])
		}
		setBusy(false)
		const bits = [`${updated} site(s) ${label}`, `${rehomed} task(s) re-homed`]
		if (missing) bits.push(`${missing} not found`)
		// Two very different failures share the `failed` list: a duplicate-task
		// skip needs the dedupe script, a busy database just needs another go.
		// Telling them apart is the difference between "call a developer" and
		// "press the button again".
		const retryable = failed.filter(f => f.retryable).length
		const skipped = failed.length - retryable
		if (skipped) bits.push(`${skipped} skipped (duplicate tasks)`)
		if (retryable) bits.push(`${retryable} not applied (database was busy — safe to retry)`)
		if (failed.length) showToast.warning(bits.join(' · '))
		else showToast.success(bits.join(' · '))
		setTarget('')
		selectedRetailerIds.value = []
		onChanged()
	}

	const doAssign = () => {
		const o = assignable.find(x => String(x.id_user) === target)
		if (!o) return
		// Deliberately NOT worded as "not-started": 'extracted' tasks are in
		// the moved set and they already have an upload behind them.
		const ok = confirm(
			`Assign ${selected.length} site(s) to ${o.email}?\n\n`
			+ `Their pending and not-yet-scored tasks (any date) move to the new mapper; `
			+ `scored/reviewed work stays with its author.`
		)
		if (ok) run(o.id_user, `assigned to ${o.email}`)
	}

	const doUnassign = () => {
		const ok = confirm(
			`Unassign ${selected.length} site(s)?\n\n`
			+ `A site with no mapper STOPS generating daily tasks, and its pending tasks `
			+ `stop showing in anyone's My Tasks until it is reassigned.`
		)
		if (ok) run(null, 'unassigned')
	}

	return <Paper
		variant="outlined"
		sx={{
			p: 1.25, mb: 2,
			bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
			borderColor: (t) => alpha(t.palette.primary.main, 0.3),
		}}
	>
		<Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
			<Typography variant="body2" sx={{ fontWeight: 700 }}>
				{selected.length} site{selected.length === 1 ? '' : 's'} selected
			</Typography>
			{outside > 0 && (
				<Typography variant="caption" color="warning.main">
					· {outside} outside the current filter
				</Typography>
			)}
			<Box sx={{ flex: 1 }} />
			<TextField
				size="small" select label="Assign to"
				value={target}
				onChange={e => setTarget(e.target.value)}
				disabled={busy}
				sx={{ width: 240, bgcolor: 'background.paper' }}
			>
				{assignable.length === 0 ? (
					<MenuItem value="" disabled>No taggers available</MenuItem>
				) : assignable.map(o => (
					<MenuItem key={o.id_user} value={String(o.id_user)}>{o.email}</MenuItem>
				))}
				{/* Owners the back would reject as a target (no longer taggers, or
				    disabled) — shown so their absence isn't a mystery. */}
				{unassignable.length > 0 && <ListSubheader>Not assignable</ListSubheader>}
				{unassignable.map(o => (
					<MenuItem key={o.id_user} value={String(o.id_user)} disabled>{o.email}</MenuItem>
				))}
			</TextField>
			<Button variant="contained" onClick={doAssign} disabled={busy || !target}>
				{busy ? <CircularProgress size={20} color="inherit" /> : 'Assign'}
			</Button>
			<Button variant="outlined" color="warning" onClick={doUnassign} disabled={busy}>
				Unassign
			</Button>
			<Button size="small" onClick={() => { selectedRetailerIds.value = [] }} disabled={busy}>
				Clear
			</Button>
		</Stack>
	</Paper>
}


function RetailersTable({ showToast, openRetailer, onChanged }) {
	const f = retailerFilters.value
	const list = useMemo(
		() => retailers.value.filter(r => filterRetailer(
			r, retailerSearch.value, retailerFilters.value, retailerCountry.value, retailerAssignee.value,
		)),
		[retailers.value, retailerSearch.value, retailerFilters.value, retailerCountry.value, retailerAssignee.value],
	)

	// Admin is the ONLY place a retailer gets reactivated, and `activeOnly`
	// is evaluated before the text match — so searching an inactive slug
	// returns nothing with no hint as to why. Count the rows the Active chip
	// is hiding from the current search so we can offer a way out.
	const hiddenByActive = useMemo(() => {
		if (!f.activeOnly || !retailerSearch.value) return 0
		return retailers.value.filter(r => !r?.active && filterRetailer(
			r, retailerSearch.value, { ...f, activeOnly: false }, retailerCountry.value, retailerAssignee.value,
		)).length
	}, [retailers.value, retailerSearch.value, retailerFilters.value, retailerCountry.value, retailerAssignee.value])

	const selected = selectedRetailerIds.value
	const selectedSet = useMemo(() => new Set(selected), [selected])
	const visibleSelected = list.filter(r => selectedSet.has(r.id_retailer)).length
	const allVisibleSelected = list.length > 0 && visibleSelected === list.length

	const toggleRow = (id) => {
		selectedRetailerIds.value = selectedSet.has(id)
			? selected.filter(x => x !== id)
			: [...selected, id]
	}
	// Select-all acts on the CURRENT FILTER only, and never drops ids that
	// live outside it — the bulk bar spells out that split in its counter.
	const toggleAllVisible = () => {
		const visibleIds = list.map(r => r.id_retailer)
		selectedRetailerIds.value = allVisibleSelected
			? selected.filter(id => !visibleIds.includes(id))
			: Array.from(new Set([...selected, ...visibleIds]))
	}

	const toggleActive = async (r) => {
		const res = await api.patch(`${BACKEND_APIS.VT}/admin/retailers/${r.id_retailer}`, { active: !r.active })
		if (!res.ok) return showToast.error(res.error)
		// With the Active pre-filter on, deactivating makes the row vanish in
		// the same click — say so instead of letting it look like a glitch.
		if (r.active && f.activeOnly) showToast.info(`'${r.slug}' deactivated — hidden by the Active filter`)
		onChanged()
	}
	const updateField = async (r, field, value) => {
		const res = await api.patch(`${BACKEND_APIS.VT}/admin/retailers/${r.id_retailer}`, { [field]: value })
		if (!res.ok) return showToast.error(res.error)
		onChanged()
	}
	const del = async (r) => {
		if (!confirm(`Delete retailer '${r.slug}'?`)) return
		const url = `${BACKEND_APIS.VT}/admin/retailers/${r.id_retailer}`
		const res = await api.del(url)
		if (res.ok) { showToast.success(_cascadeMsg('Retailer deleted', res.data?.cascade)); return onChanged() }
		if (res.status === 409 && res.data) {
			const sc = res.data.section_count || 0
			const tc = res.data.task_count || 0
			const rc = res.data.run_count || 0
			const ok = confirm(
				`This retailer has ${sc} section(s), ${tc} task(s) and ${rc} run(s).\n\n` +
				`Delete EVERYTHING in cascade? This cannot be undone.`
			)
			if (!ok) return
			const res2 = await api.del(url + '?force=true')
			if (!res2.ok) return showToast.error(res2.error)
			showToast.success(_cascadeMsg('Retailer cascade-deleted', res2.data?.cascade))
			return onChanged()
		}
		showToast.error(res.error)
	}

	return <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
		<Stack direction="row" sx={{
			px: 2, py: 1, alignItems: 'center',
			bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
		}}>
			<StorefrontIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
			<Typography variant="caption" color="text.secondary">
				<Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{list.length}</Box>
				{' '}of {retailers.value.length} retailer{retailers.value.length === 1 ? '' : 's'}
			</Typography>
			{hiddenByActive > 0 && (
				<Typography variant="caption" color="warning.main" sx={{ ml: 2 }}>
					{hiddenByActive} match{hiddenByActive === 1 ? '' : 'es'} hidden by the Active filter{' · '}
					<Link
						component="button"
						variant="caption"
						underline="always"
						onClick={() => { retailerFilters.value = { ...f, activeOnly: false } }}
					>
						show inactive
					</Link>
				</Typography>
			)}
		</Stack>
		<Divider />
		<TableContainer>
			<Table size="small" stickyHeader sx={{
				// Zebra striping for 295+ rows readability.
				'& tbody tr:nth-of-type(odd)': {
					bgcolor: (t) => alpha(t.palette.text.primary, 0.015),
				},
				'& tbody tr': {
					transition: 'background-color 120ms ease',
					borderLeft: '3px solid transparent',
				},
				// Hover overrides zebra — primary tint + accent left border.
				'& tbody tr:hover': {
					bgcolor: (t) => `${alpha(t.palette.primary.main, 0.06)} !important`,
					borderLeftColor: (t) => t.palette.primary.main,
				},
				'& thead th': {
					bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
					color: 'text.primary',
					fontWeight: 700,
					fontSize: '0.72rem',
					textTransform: 'uppercase',
					letterSpacing: 0.4,
				},
				'& tbody td': {
					py: 1,
				},
			}}>
				<TableHead>
					<TableRow>
						<TableCell padding="checkbox">
							<Tooltip title={list.length === 0
								? 'Nothing to select'
								: allVisibleSelected ? 'Deselect the filtered sites' : 'Select the filtered sites'}>
								<Checkbox
									size="small"
									disabled={list.length === 0}
									checked={allVisibleSelected}
									indeterminate={visibleSelected > 0 && !allVisibleSelected}
									onChange={toggleAllVisible}
								/>
							</Tooltip>
						</TableCell>
						<TableCell>Slug</TableCell>
						<TableCell>Name</TableCell>
						<TableCell align="center">QA</TableCell>
						<TableCell>puig</TableCell>
						<TableCell>retail</TableCell>
						<TableCell align="center">Active</TableCell>
						<TableCell align="center">NL</TableCell>
						<TableCell align="center">Mobile doc</TableCell>
						<TableCell align="right">Sections</TableCell>
						<TableCell align="right">Actions</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{list.length === 0 ? (
						<TableRow><TableCell colSpan={11} align="center" sx={{ py: 5, color: 'text.secondary' }}>
							<Typography variant="body2">No retailers match the current filter.</Typography>
						</TableCell></TableRow>
					) : list.map(r => (
						<TableRow key={r.id_retailer} selected={selectedSet.has(r.id_retailer)}>
							<TableCell padding="checkbox">
								<Checkbox
									size="small"
									checked={selectedSet.has(r.id_retailer)}
									onClick={e => e.stopPropagation()}
									onChange={() => toggleRow(r.id_retailer)}
								/>
							</TableCell>
							<TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'primary.main' }}>{r.slug}</TableCell>
							<TableCell>{r.name}</TableCell>
							<TableCell align="center"><QaCell retailer={r} showToast={showToast} onChanged={onChanged} /></TableCell>
							<TableCell sx={{ width: 90 }}>
								<TextField size="small" variant="standard" type="number"
									defaultValue={r.puig_id_site ?? ''}
									onBlur={e => {
										const v = e.target.value
										if ((v === '' ? null : Number(v)) !== r.puig_id_site) updateField(r, 'puig_id_site', v)
									}}
									inputProps={{ style: { fontSize: 12 } }}
								/>
							</TableCell>
							<TableCell sx={{ width: 90 }}>
								<TextField size="small" variant="standard" type="number"
									defaultValue={r.retail_id_site ?? ''}
									onBlur={e => {
										const v = e.target.value
										if ((v === '' ? null : Number(v)) !== r.retail_id_site) updateField(r, 'retail_id_site', v)
									}}
									inputProps={{ style: { fontSize: 12 } }}
								/>
							</TableCell>
							<TableCell align="center">
								<Tooltip title={r.active ? 'Active — click to deactivate' : 'Inactive — click to activate'}>
									<Chip
										size="small"
										icon={r.active
											? <CheckCircleIcon style={{ fontSize: 14 }} />
											: <RemoveCircleOutlineIcon style={{ fontSize: 14 }} />}
										label={r.active ? 'active' : 'inactive'}
										color={r.active ? 'success' : 'default'}
										variant={r.active ? 'filled' : 'outlined'}
										onClick={() => toggleActive(r)}
										sx={{ fontWeight: 600, minWidth: 78 }}
									/>
								</Tooltip>
							</TableCell>
							<TableCell align="center">
								<Tooltip title={r.has_newsletter
									? 'Newsletter section enabled'
									: 'No newsletter section'}>
									<Chip
										size="small"
										icon={<EmailIcon style={{ fontSize: 14 }} />}
										label={r.has_newsletter ? 'yes' : 'no'}
										color={r.has_newsletter ? 'info' : 'default'}
										variant={r.has_newsletter ? 'filled' : 'outlined'}
										sx={{ fontWeight: 600, minWidth: 64 }}
									/>
								</Tooltip>
							</TableCell>
							<TableCell align="center">
								<DocUaCell retailer={r} onToggle={() => {
									const msg = r.doc_ua
										? `Stop requesting '${r.slug}' pages as a phone?`
										: `Request '${r.slug}' pages as a phone for EVERY operator, from their next session?\n\nIt switches itself off if the store starts blocking it.`
									if (confirm(msg)) updateField(r, 'doc_ua', !r.doc_ua)
								}} />
							</TableCell>
							<TableCell align="right">
								<Tooltip title={
									r.section_count > 0
										? `${r.active_section_count ?? 0} active / ${r.section_count} total — click to open`
										: 'No sections yet — click to add'
								}>
									<Button
										size="small"
										variant="outlined"
										endIcon={<ChevronRightIcon fontSize="small" />}
										onClick={() => openRetailer(r)}
										sx={{
											textTransform: 'none',
											borderRadius: 6,
											px: 1.25,
											minWidth: 0,
											fontVariantNumeric: 'tabular-nums',
										}}
									>
										<Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
											{r.section_count > 0
												? `${r.active_section_count ?? 0}/${r.section_count}`
												: 0}
										</Box>
										<Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
											section{r.section_count === 1 ? '' : 's'}
										</Box>
									</Button>
								</Tooltip>
							</TableCell>
							<TableCell align="right">
								<Tooltip title="Delete retailer">
									<IconButton size="small" onClick={() => del(r)}
										sx={{
											color: 'text.disabled',
											'&:hover': { color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
										}}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	</Paper>
}


// ────────────────────────────────────────────────────────────────────
// QA cell (avatar + menu) — unchanged from the previous design
// ────────────────────────────────────────────────────────────────────
const getInitials = (name) => {
	if (!name) return '?'
	const parts = name.trim().split(/[\s@.]+/).filter(Boolean)
	if (parts.length === 0) return '?'
	if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}
const stringToColor = (s) => {
	if (!s) return '#757575'
	let hash = 0
	for (let i = 0; i < s.length; i++) hash = s.charCodeAt(i) + ((hash << 5) - hash)
	return `hsl(${hash % 360}, 60%, 50%)`
}

// Mobile document (869f6xzqv): whether the extension requests this retailer's
// pages as a phone. Some stores pick their markup per device and give the
// desktop one to the operator's Chrome, and the crops come out displaced.
// The counters are the evidence to decide on, all from the last 7 days:
// sessions where the extension saw the page wider than the phone, sessions
// where the operator pressed "Reload as mobile", and deny pages — with the
// mobile document on, which is what can make turning it on a bad idea. Two
// denied sessions with it on switch it off on their own (the ⚠).
function DocUaCell({ retailer: r, onToggle }) {
	const ev = r.doc_ua_evidence || {}
	const sessions = ev.sessions || 0
	const desktop = ev.desktop_tasks || 0
	const manual = ev.manual_reload_tasks || 0
	const denied = ev.deny_mobile_tasks || 0
	const autoOff = !r.doc_ua && r.doc_ua_auto_off_at
	const hasSignal = desktop > 0 || manual > 0
	const tip = [
		r.doc_ua
			? 'Pages are requested as a phone — click to stop'
			: 'Pages are requested as the operator\'s Chrome — click to request them as a phone',
		`Last 7 days: ${sessions} saved session${sessions === 1 ? '' : 's'}`,
		`· desktop version detected in ${desktop}${ev.max_viewport_width ? ` (up to ${ev.max_viewport_width} px)` : ''}`,
		`· "Reload as mobile" pressed in ${manual}`,
		`· blocked with the mobile document on: ${denied}`,
		autoOff ? `Switched off on its own ${r.doc_ua_auto_off_at.slice(0, 10)}: ${r.doc_ua_auto_off_reason || ''}` : null,
	].filter(Boolean).join('\n')
	return <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tip}</span>}>
		<Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
			<Chip
				size="small"
				icon={<PhoneIphoneIcon style={{ fontSize: 14 }} />}
				label={r.doc_ua ? 'on' : 'off'}
				color={r.doc_ua ? 'success' : (hasSignal ? 'warning' : 'default')}
				variant={r.doc_ua ? 'filled' : 'outlined'}
				onClick={onToggle}
				sx={{ fontWeight: 600, minWidth: 58 }}
			/>
			{(hasSignal || denied > 0) && (
				<Typography variant="caption" color={denied > 0 ? 'error.main' : 'text.secondary'} sx={{ whiteSpace: 'nowrap' }}>
					{desktop}/{sessions}{manual > 0 ? ` · ↻${manual}` : ''}{denied > 0 ? ` · ⛔${denied}` : ''}
				</Typography>
			)}
			{autoOff && <WarningAmberIcon color="warning" style={{ fontSize: 16 }} />}
		</Stack>
	</Tooltip>
}

function QaCell({ retailer, showToast, onChanged }) {
	const [anchorEl, setAnchorEl] = useState(null)
	const [busy, setBusy] = useState(false)
	const open = Boolean(anchorEl)
	const current = retailer.qa
	const handleOpen = useCallback(e => { e.stopPropagation(); setAnchorEl(e.currentTarget) }, [])
	const handleClose = useCallback(() => setAnchorEl(null), [])
	const assign = async (id_user) => {
		handleClose()
		setBusy(true)
		const r = await api.patch(
			`${BACKEND_APIS.VT}/admin/retailers/${retailer.id_retailer}/assign-user`,
			{ id_user },
		)
		setBusy(false)
		if (!r.ok) return showToast.error(r.error)
		showToast.success(id_user ? 'QA assigned' : 'QA unassigned')
		onChanged()
	}
	if (busy) return <Box sx={{ display: 'inline-flex' }}><CircularProgress size={24} /></Box>
	return <>
		<Tooltip title={current ? current.email : 'Click to assign QA'}>
			<Avatar onClick={handleOpen}
				sx={{
					width: 28, height: 28, fontSize: '0.75rem', cursor: 'pointer',
					bgcolor: current ? stringToColor(current.email) : '#e0e0e0',
					color: current ? '#fff' : '#757575',
					'&:hover': { opacity: 0.85, transform: 'scale(1.05)' },
					transition: 'all 0.15s',
					mx: 'auto',
				}}>
				{current ? getInitials(current.email) : '+'}
			</Avatar>
		</Tooltip>
		<Menu anchorEl={anchorEl} open={open} onClose={handleClose}
			PaperProps={{ sx: { maxHeight: 320, width: 260 } }}>
			{current && (
				<MenuItem onClick={() => assign(null)} sx={{ color: 'error.main' }}>
					<Typography variant="body2">Unassign</Typography>
				</MenuItem>
			)}
			{current && <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 0.5 }} />}
			{taggers.value.length === 0 ? (
				<MenuItem disabled>
					<Typography variant="body2" color="text.secondary">No taggers available</Typography>
				</MenuItem>
			) : taggers.value.map(t => (
				<MenuItem key={t.id_user} onClick={() => assign(t.id_user)}
					selected={current?.id_user === t.id_user}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						<Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem', bgcolor: stringToColor(t.email) }}>
							{getInitials(t.email)}
						</Avatar>
						<Typography variant="body2">{t.email}</Typography>
					</Box>
				</MenuItem>
			))}
		</Menu>
	</>
}


// ────────────────────────────────────────────────────────────────────
// Create retailer dialog (replaces the inline form)
// ────────────────────────────────────────────────────────────────────
function CreateRetailerDialog({ open, onClose, showToast, onChanged }) {
	const [form, setForm] = useState({ slug: '', name: '', puig_id_site: '', retail_id_site: '' })
	useEffect(() => { if (open) setForm({ slug: '', name: '', puig_id_site: '', retail_id_site: '' }) }, [open])
	const submit = async () => {
		if (!form.slug || !form.name) return showToast.warning('Slug and name required')
		if (!isValidSlug(form.slug)) return showToast.warning(`Invalid slug · ${SLUG_HELP}`)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/retailers`, form)
		if (!r.ok) return showToast.error(r.error)
		showToast.success('Retailer created')
		onChanged()
		onClose()
	}
	return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
		<DialogTitle>Add retailer</DialogTitle>
		<DialogContent>
			<Stack spacing={2} sx={{ mt: 1 }}>
				<TextField size="small" label="slug" value={form.slug}
					onChange={e => setForm({ ...form, slug: e.target.value })}
					error={form.slug !== '' && !isValidSlug(form.slug)}
					helperText={form.slug && !isValidSlug(form.slug) ? SLUG_HELP : ' '}
				/>
				<TextField size="small" label="name" value={form.name}
					onChange={e => setForm({ ...form, name: e.target.value })} />
				<Stack direction="row" spacing={2}>
					<TextField size="small" label="puig id_site" type="number" sx={{ flex: 1 }}
						value={form.puig_id_site}
						onChange={e => setForm({ ...form, puig_id_site: e.target.value })} />
					<TextField size="small" label="retail id_site" type="number" sx={{ flex: 1 }}
						value={form.retail_id_site}
						onChange={e => setForm({ ...form, retail_id_site: e.target.value })} />
				</Stack>
			</Stack>
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose}>Cancel</Button>
			<Button variant="contained" onClick={submit}>Create</Button>
		</DialogActions>
	</Dialog>
}


// ────────────────────────────────────────────────────────────────────
// Sections drawer
// ────────────────────────────────────────────────────────────────────
const NEWSLETTER_URL_PREFIX = 'https://storage.googleapis.com/'
const SECTION_FORM_DEFAULTS = { slug: '', name: '', url: '', kind: 'web', puig_id_section: '', retail_id_section: '' }


function filterSection(s, search, f) {
	if (!s) return false
	if (f.kindWeb        && (s.kind || 'web') !== 'web') return false
	if (f.kindNewsletter && (s.kind || 'web') !== 'newsletter') return false
	if (f.hasPuig        && s.puig_id_section   == null) return false
	if (f.hasRetail      && s.retail_id_section == null) return false
	if (f.activeOnly     && !s.active) return false
	if (f.inactiveOnly   &&  s.active) return false
	if (!search) return true
	const q = String(search).toLowerCase()
	return (
		(s.slug || '').toLowerCase().includes(q)
		|| (s.name || '').toLowerCase().includes(q)
		|| (s.url  || '').toLowerCase().includes(q)
		|| String(s.puig_id_section   || '').includes(q)
		|| String(s.retail_id_section || '').includes(q)
	)
}


function SectionsDrawer({ open, onClose, showToast, onChanged }) {
	const [form, setForm] = useState(SECTION_FORM_DEFAULTS)
	const [showAdd, setShowAdd] = useState(false)
	const [historyId, setHistoryId] = useState(null)   // section whose history drawer is open
	const retailer = retailers.value.find(r => r.id_retailer === drawerRetailerId.value) || null
	const list = useMemo(
		() => sections.value.filter(s => filterSection(s, sectionSearch.value, sectionFilters.value)),
		[sections.value, sectionSearch.value, sectionFilters.value],
	)

	useEffect(() => {
		if (!open) { setShowAdd(false); setForm(SECTION_FORM_DEFAULTS) }
	}, [open])

	const isNewsletter = form.kind === 'newsletter'
	const urlInvalid = isNewsletter && form.url !== '' && !form.url.startsWith(NEWSLETTER_URL_PREFIX)

	const create = async () => {
		if (!retailer) return
		if (!form.slug || !form.name || !form.url) return showToast.warning('slug, name and url are required')
		if (!isValidSlug(form.slug)) return showToast.warning(`Invalid slug · ${SLUG_HELP}`)
		if (urlInvalid) return showToast.warning(`Newsletter URL must start with ${NEWSLETTER_URL_PREFIX}`)
		const r = await api.post(`${BACKEND_APIS.VT}/admin/sections`, {
			...form, id_retailer: retailer.id_retailer,
		})
		if (!r.ok) return showToast.error(r.error)
		showToast.success('Section created')
		setForm(SECTION_FORM_DEFAULTS)
		setShowAdd(false)
		onChanged()
	}

	const updateField = async (s, field, value) => {
		const r = await api.patch(`${BACKEND_APIS.VT}/admin/sections/${s.id_section}`, { [field]: value })
		if (!r.ok) return showToast.error(r.error)
		onChanged()
	}
	const del = async (s) => {
		if (!confirm(`Delete section '${s.slug}'?`)) return
		const url = `${BACKEND_APIS.VT}/admin/sections/${s.id_section}`
		const r = await api.del(url)
		if (r.ok) { showToast.success(_cascadeMsg('Section deleted', r.data?.cascade)); return onChanged() }
		if (r.status === 409 && r.data) {
			const tc = r.data.task_count || 0
			const rc = r.data.run_count || 0
			const ok = confirm(
				`This section has ${tc} task(s) and ${rc} run(s).\n\n` +
				`Delete EVERYTHING in cascade? This cannot be undone.`
			)
			if (!ok) return
			const r2 = await api.del(url + '?force=true')
			if (!r2.ok) return showToast.error(r2.error)
			showToast.success(_cascadeMsg('Section cascade-deleted', r2.data?.cascade))
			return onChanged()
		}
		showToast.error(r.error)
	}

	const f = sectionFilters.value
	const setSectionFilter = (k) => () => { sectionFilters.value = { ...f, [k]: !f[k] } }

	return <Drawer
		anchor="right" open={open} onClose={onClose}
		PaperProps={{ sx: { width: { xs: '100%', sm: '85%', md: '70%', lg: '60%' } } }}
	>
		{!retailer ? null : (
			<Box>
				<Box sx={{
					px: 2, py: 1.5,
					bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
					borderBottom: '1px solid',
					borderColor: 'divider',
				}}>
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<StorefrontIcon sx={{ color: 'primary.main' }} />
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography variant="overline" sx={{ display: 'block', lineHeight: 1, color: 'text.secondary', letterSpacing: 0.6 }}>
								Sections
							</Typography>
							<Typography variant="h6" sx={{ lineHeight: 1.2 }}>{retailer.name}</Typography>
						</Box>
						<Chip size="small"
							label={retailer.puig_id_site   ? `puig:${retailer.puig_id_site}`     : 'no puig'}
							color={retailer.puig_id_site ? 'info' : 'default'}
							variant={retailer.puig_id_site ? 'filled' : 'outlined'}
							sx={{ fontWeight: 600 }} />
						<Chip size="small"
							label={retailer.retail_id_site ? `retail:${retailer.retail_id_site}` : 'no retail'}
							color={retailer.retail_id_site ? 'info' : 'default'}
							variant={retailer.retail_id_site ? 'filled' : 'outlined'}
							sx={{ fontWeight: 600 }} />
						<IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
					</Stack>
				</Box>
				<Box sx={{ p: 2 }}>

				<Stack direction="row" spacing={1} sx={{ mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
					<TextField
						size="small"
						placeholder="Search slug, name, url…"
						value={sectionSearch.value}
						onChange={e => { sectionSearch.value = e.target.value }}
						sx={{ minWidth: 240 }}
						InputProps={{
							startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
							endAdornment: sectionSearch.value ? (
								<InputAdornment position="end">
									<IconButton size="small" onClick={() => { sectionSearch.value = '' }}>
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : null,
						}}
					/>
					<FilterChip label="Web"        active={f.kindWeb}        onClick={setSectionFilter('kindWeb')} />
					<FilterChip label="Newsletter" active={f.kindNewsletter} onClick={setSectionFilter('kindNewsletter')} />
					<FilterChip label="Has Puig"   active={f.hasPuig}        onClick={setSectionFilter('hasPuig')} />
					<FilterChip label="Has Retail" active={f.hasRetail}      onClick={setSectionFilter('hasRetail')} />
					<FilterChip label="Active"     active={f.activeOnly}     onClick={setSectionFilter('activeOnly')} />
					<FilterChip label="Inactive"   active={f.inactiveOnly}   onClick={setSectionFilter('inactiveOnly')}   color="warning" />
					<Box sx={{ flex: 1 }} />
					<Button size="small" variant="contained" onClick={() => setShowAdd(v => !v)} startIcon={<AddIcon />}>
						Add section
					</Button>
				</Stack>

				{showAdd && (
					<Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
						<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
							<TextField size="small" label="slug" sx={{ width: 130 }} value={form.slug}
								onChange={e => setForm({ ...form, slug: e.target.value })}
								error={form.slug !== '' && !isValidSlug(form.slug)}
								helperText={form.slug && !isValidSlug(form.slug) ? SLUG_HELP : ' '} />
							<TextField size="small" label="name" sx={{ width: 150 }} value={form.name}
								onChange={e => setForm({ ...form, name: e.target.value })} />
							<FormControl size="small" sx={{ width: 130 }}>
								<InputLabel id="dr-section-kind-label">kind</InputLabel>
								<Select labelId="dr-section-kind-label" label="kind"
									value={form.kind}
									onChange={e => setForm({ ...form, kind: e.target.value })}>
									<MenuItem value="web">web</MenuItem>
									<MenuItem value="newsletter">newsletter</MenuItem>
								</Select>
							</FormControl>
							<TextField size="small"
								label={isNewsletter ? 'GCS folder URL' : 'url'}
								placeholder={isNewsletter ? `${NEWSLETTER_URL_PREFIX}vt-newsletter/<retailer>` : ''}
								sx={{ flex: 1, minWidth: 200 }}
								value={form.url}
								onChange={e => setForm({ ...form, url: e.target.value })}
								error={urlInvalid}
								helperText={urlInvalid ? `must start with ${NEWSLETTER_URL_PREFIX}` : ' '} />
							<TextField size="small" label="puig id" type="number" sx={{ width: 90 }}
								value={form.puig_id_section}
								onChange={e => setForm({ ...form, puig_id_section: e.target.value })} />
							<TextField size="small" label="retail id" type="number" sx={{ width: 90 }}
								value={form.retail_id_section}
								onChange={e => setForm({ ...form, retail_id_section: e.target.value })} />
							<Button variant="contained" onClick={create} sx={{ alignSelf: 'flex-start', mt: '2px' }}>
								Create
							</Button>
						</Stack>
					</Paper>
				)}

				<Paper variant="outlined" sx={{ overflow: 'hidden' }}>
					<Stack direction="row" sx={{
						px: 2, py: 1, alignItems: 'center',
						bgcolor: (t) => alpha(t.palette.text.primary, 0.02),
					}}>
						<Typography variant="caption" color="text.secondary">
							<Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{list.length}</Box>
							{' '}of {sections.value.length} section{sections.value.length === 1 ? '' : 's'}
						</Typography>
					</Stack>
					<Divider />
					<TableContainer sx={{ maxHeight: 'calc(100vh - 240px)' }}>
						<Table size="small" stickyHeader sx={{
							'& tbody tr:nth-of-type(odd)': {
								bgcolor: (t) => alpha(t.palette.text.primary, 0.015),
							},
							'& tbody tr': {
								transition: 'background-color 120ms ease',
								borderLeft: '3px solid transparent',
							},
							'& tbody tr:hover': {
								bgcolor: (t) => `${alpha(t.palette.primary.main, 0.06)} !important`,
								borderLeftColor: (t) => t.palette.primary.main,
							},
							'& thead th': {
								bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
								fontWeight: 700,
								fontSize: '0.72rem',
								textTransform: 'uppercase',
								letterSpacing: 0.4,
							},
							'& tbody td': { py: 1 },
						}}>
							<TableHead>
								<TableRow>
									<TableCell>Slug</TableCell>
									<TableCell>Name</TableCell>
									<TableCell>Kind</TableCell>
									<TableCell>Active</TableCell>
									<TableCell>URL</TableCell>
									<TableCell>puig</TableCell>
									<TableCell>retail</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{list.length === 0 ? (
									<TableRow><TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
										<Typography variant="body2">
											{sections.value.length === 0 ? 'No sections yet.' : 'No sections match the current filter.'}
										</Typography>
									</TableCell></TableRow>
								) : list.map(s => (
									<TableRow key={s.id_section}>
										<TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'primary.main' }}>{s.slug}</TableCell>
										<TableCell>{s.name}</TableCell>
										<TableCell>
											<Chip size="small" label={s.kind || 'web'}
												color={s.kind === 'newsletter' ? 'info' : 'default'}
												variant={s.kind === 'newsletter' ? 'filled' : 'outlined'}
												sx={{ fontWeight: 600 }} />
										</TableCell>
										<TableCell>
											<Tooltip title={s.active
												? 'Active — section appears in Pipeline and gets new daily tasks. Click to pause.'
												: 'Paused — section is hidden from Pipeline and no new tasks are created for it. Click to activate.'
											}>
												<Chip size="small"
													label={s.active ? 'active' : 'paused'}
													color={s.active ? 'success' : 'default'}
													variant={s.active ? 'filled' : 'outlined'}
													onClick={() => updateField(s, 'active', !s.active)}
													sx={{ fontWeight: 600, cursor: 'pointer' }}
												/>
											</Tooltip>
										</TableCell>
										<TableCell sx={{ maxWidth: 260 }}>
											<Typography variant="caption" sx={{ wordBreak: 'break-all', color: 'text.secondary' }}>{s.url}</Typography>
										</TableCell>
										<TableCell sx={{ width: 90 }}>
											<TextField size="small" variant="standard" type="number"
												defaultValue={s.puig_id_section ?? ''}
												onBlur={e => {
													const v = e.target.value
													if ((v === '' ? null : Number(v)) !== s.puig_id_section) updateField(s, 'puig_id_section', v)
												}}
												inputProps={{ style: { fontSize: 12 } }} />
										</TableCell>
										<TableCell sx={{ width: 90 }}>
											<TextField size="small" variant="standard" type="number"
												defaultValue={s.retail_id_section ?? ''}
												onBlur={e => {
													const v = e.target.value
													if ((v === '' ? null : Number(v)) !== s.retail_id_section) updateField(s, 'retail_id_section', v)
												}}
												inputProps={{ style: { fontSize: 12 } }} />
										</TableCell>
										<TableCell align="right">
											<Tooltip title="Execution history — every date this section ran">
												<IconButton size="small" onClick={() => setHistoryId(s.id_section)}>
													<HistoryIcon fontSize="small" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete section">
												<IconButton size="small" onClick={() => del(s)}
													sx={{
														color: 'text.disabled',
														'&:hover': { color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
													}}>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
				</Box>
			</Box>
		)}
		<SectionHistoryDrawer idSection={historyId} onClose={() => setHistoryId(null)} />
	</Drawer>
}


// ────────────────────────────────────────────────────────────────────
// Bulk import dialog
// ────────────────────────────────────────────────────────────────────
function BulkImportDialog({ open, onClose, showToast, onChanged }) {
	const [what, setWhat] = useState('retailers')   // 'retailers' | 'sections'
	const [client, setClient] = useState('puig')
	const [file, setFile] = useState(null)
	const [busy, setBusy] = useState(false)
	const [summary, setSummary] = useState(null)

	useEffect(() => {
		if (open) { setWhat('retailers'); setClient('puig'); setFile(null); setSummary(null); setBusy(false) }
	}, [open])

	const isSections = what === 'sections'
	const endpoint = isSections ? 'sections/bulk-import' : 'retailers/bulk-import'
	const expectedCols = isSections ? 'name, slug, id_site, id_section, section_url' : 'id_site, name, slug'

	const submit = async () => {
		if (!file) return showToast.warning('Pick a CSV file first')
		setBusy(true)
		setSummary(null)
		const fd = new FormData()
		fd.append('file', file)
		// Custom raw fetch so we can send multipart — vtApi.post serialises JSON.
		try {
			const res = await fetch(
				`${BACKEND_APIS.VT}/admin/${endpoint}?client=${encodeURIComponent(client)}`,
				{ method: 'POST', credentials: 'include', body: fd },
			)
			const txt = await res.text()
			let data = null
			try { data = txt ? JSON.parse(txt) : null } catch {}
			setBusy(false)
			if (!res.ok) {
				return showToast.error('Bulk import failed: ' + ((data && data.error) || txt || res.status))
			}
			setSummary(data)
			onChanged()
		} catch (e) {
			setBusy(false)
			showToast.error('Bulk import failed: ' + (e.message || e))
		}
	}

	return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
		<DialogTitle>Bulk import</DialogTitle>
		<DialogContent>
			<Stack spacing={2} sx={{ mt: 1 }}>
				<Stack direction="row" spacing={2}>
					<FormControl size="small" sx={{ width: 200 }}>
						<InputLabel id="bulk-what-label">import</InputLabel>
						<Select labelId="bulk-what-label" label="import"
							value={what} onChange={e => { setWhat(e.target.value); setSummary(null) }}>
							<MenuItem value="retailers">Retailers (sites)</MenuItem>
							<MenuItem value="sections">Sections</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ width: 160 }}>
						<InputLabel id="bulk-client-label">client</InputLabel>
						<Select labelId="bulk-client-label" label="client"
							value={client} onChange={e => setClient(e.target.value)}>
							<MenuItem value="puig">puig</MenuItem>
							<MenuItem value="retail">retail</MenuItem>
						</Select>
					</FormControl>
				</Stack>
				<Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
					CSV columns expected: <b>{expectedCols}</b> (header row required).
					{isSections ? (
						<> Idempotent: section matched within its retailer by <b>{`${client}_id_section`}</b> or
						by slug. The retailer must already exist (matched by <b>{`${client}_id_site`}</b>) —
						import retailers first; rows for unknown sites are reported as conflicts.
						Slugs are sanitised; all sections import as <b>web</b>.</>
					) : (
						<> Idempotent: retailer matched by <b>{`${client}_id_site`}</b> or by slug; rows
						that conflict (slug already maps to a different id_site) are reported below
						instead of being applied.</>
					)}
				</Alert>
				<Box>
					<Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
						{file ? `Selected: ${file.name}` : 'Choose CSV'}
						<input hidden type="file" accept=".csv,text/csv"
							onChange={e => setFile(e.target.files?.[0] || null)} />
					</Button>
				</Box>

				{summary && <SummaryView summary={summary} />}
			</Stack>
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose}>Close</Button>
			<Button variant="contained" onClick={submit} disabled={busy || !file}>
				{busy ? <CircularProgress size={20} /> : 'Import'}
			</Button>
		</DialogActions>
	</Dialog>
}


function SummaryView({ summary }) {
	const cards = [
		{ k: 'Created',   v: summary.created,                 c: 'success' },
		{ k: 'Updated',   v: summary.updated,                 c: 'info' },
		{ k: 'Matched',   v: summary.matched,                 c: 'default' },
		{ k: 'Conflicts', v: summary.conflicts?.length || 0,  c: (summary.conflicts?.length ? 'error' : 'default') },
		{ k: 'Rows seen', v: summary.rows_seen,               c: 'default' },
	]
	return <Box>
		<Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
			{cards.map(c => (
				<Paper
					key={c.k}
					variant="outlined"
					sx={{
						px: 2, py: 1, minWidth: 110, flex: '0 0 auto',
						borderColor: (t) => (c.c === 'default' ? 'divider' : alpha(t.palette[c.c].main, 0.4)),
						bgcolor: (t) => (c.c === 'default' ? alpha(t.palette.text.primary, 0.02) : alpha(t.palette[c.c].main, 0.06)),
					}}
				>
					<Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', letterSpacing: 0.3 }}>
						{c.k}
					</Typography>
					<Typography variant="h6" sx={{
						lineHeight: 1.2,
						color: c.c === 'default' ? 'text.primary' : `${c.c}.main`,
						fontWeight: 700,
					}}>
						{c.v}
					</Typography>
				</Paper>
			))}
		</Stack>
		{summary.conflicts && summary.conflicts.length > 0 && (
			<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>Kind</TableCell>
							<TableCell>Identifier</TableCell>
							<TableCell>Reason</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{summary.conflicts.map((c, i) => (
							<TableRow key={i}>
								<TableCell>{c.kind}</TableCell>
								<TableCell>{typeof c.identifier === 'string' ? c.identifier : JSON.stringify(c.identifier)}</TableCell>
								<TableCell>{c.reason}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		)}
	</Box>
}
